import { type AxiosInstance } from "axios";
import { z } from "zod";

// Keys are camelCase here; useApiClient converts them to and from snake_case.

export const V2_SEARCH_URL = "/all-matters-analyze/v2/search";
export const V2_SEARCH_CHOICES_URL = "/all-matters-analyze/v2/search/choices";

export const V2_SEARCH_PAGE_SIZES = [20, 50, 100] as const;
export type V2SearchPageSize = (typeof V2_SEARCH_PAGE_SIZES)[number];
export const V2_SEARCH_DEFAULT_PAGE_SIZE: V2SearchPageSize = 50;

export const V2_SEARCH_SORTS = [
  { value: "-picture_acquired_at", label: "取得日の新しい順" },
  { value: "picture_acquired_at", label: "取得日の古い順" },
  { value: "location", label: "所在・番号の順" },
  { value: "-land_area_m2", label: "地積の大きい順" },
  { value: "land_area_m2", label: "地積の小さい順" },
  { value: "-floor_area_m2", label: "床面積の大きい順" },
  { value: "floor_area_m2", label: "床面積の小さい順" },
  { value: "-new_construction_date", label: "新築年月日の新しい順" },
  { value: "new_construction_date", label: "新築年月日の古い順" },
] as const;
export type V2SearchSort = (typeof V2_SEARCH_SORTS)[number]["value"];
export const V2_SEARCH_DEFAULT_SORT: V2SearchSort = "-picture_acquired_at";

export const REAL_ESTATE_KINDS = ["土地", "建物", "区分建物"] as const;
export const JUDGEMENT_STATUSES = ["確定", "要確認", "判定不能"] as const;

export interface NumberRange {
  min: number | null;
  max: number | null;
}
export interface DateRange {
  from: string | null;
  to: string | null;
}

/** Omitted keys mean "no condition". */
export interface V2SearchRequest {
  property?: {
    kinds?: string[];
    prefectureIds?: number[];
    prefectureCityIds?: number[];
    location?: string;
    locationIncludeHistory?: boolean;
    number?: string;
    realEstateNumber?: string;
    buildingName?: string;
    landCategories?: string[];
    landAreaM2?: NumberRange;
    landAreaSources?: string[];
    buildingTypes?: string[];
    structureCategories?: string[];
    floorAreaM2?: NumberRange;
    newConstructionDate?: DateRange;
    newConstructionDateStatuses?: string[];
    aboveGroundFloors?: NumberRange;
    closed?: "include" | "exclude" | "only";
    oneBuildingFloorAreaM2?: NumberRange;
    unitFloor?: NumberRange;
    hasAnnex?: boolean;
    floorAreaWithAnnexM2?: NumberRange;
    titleOwnerName?: string;
    titleOwnerAddress?: string;
    siteLocation?: string;
    siteRightKinds?: string[];
  };
  owner?: {
    name?: string;
    address?: string;
    includeHistory?: boolean;
    isCorporation?: boolean;
    corporateNumber?: string;
    companyRegistrationNumber?: string;
    count?: NumberRange;
    cautions?: string[];
    hasExternalCharacter?: boolean;
  };
  koku?: {
    dispositionRestrictions?: string[];
    restrictionsIncludeHistory?: boolean;
    purposeRightKinds?: string[];
    purposeActions?: string[];
    reasonWords?: string[];
    acceptanceDate?: DateRange;
    reasonDate?: DateRange;
    hasProvisional?: boolean;
    trustPartyName?: string;
  };
  security?: {
    exists?: boolean;
    includeHistory?: boolean;
    holderName?: string;
    debtorName?: string;
    debtorAddress?: string;
    rightKinds?: string[];
    acceptanceDate?: DateRange;
    currentAmountYen?: NumberRange;
    reasonWords?: string[];
    effectiveRank?: NumberRange;
    handlingBranch?: string;
    isProvisional?: boolean;
    principalFixed?: boolean;
    hasChanges?: boolean;
    jointSecurityPropertiesUndetermined?: boolean;
    hasJointSecurity?: boolean;
    jointCatalogNumber?: string;
    jointItemLocation?: string;
    jointItemNumber?: string;
    usageRightHolderName?: string;
  };
  acquisition?: {
    pictureAcquiredAt?: DateRange;
    requestName?: string;
    requestId?: number;
    mineOnly?: boolean;
  };
  judgement?: {
    ownerStatuses?: string[];
    securityStatuses?: string[];
  };
  onePerProperty: boolean;
  sort: V2SearchSort;
  page: number;
  pageSize: V2SearchPageSize;
}

const judgementSchema = z.enum(JUDGEMENT_STATUSES).nullable();

/** Schema 6.3 空の理由. Older backends omit these keys. */
export const EMPTY_REASONS = [
  "記録なし",
  "所有権の登記なし",
  "共用部分",
  "敷地権",
  "不明",
] as const;
export type EmptyReason = (typeof EMPTY_REASONS)[number];
// An unknown future reason falls back to the certainty-based wording.
const emptyReasonSchema = z
  .string()
  .nullish()
  .transform(
    (value) => EMPTY_REASONS.find((reason) => reason === value) ?? null
  );
const judgementReasonSchema = z.string().nullish();

export const v2SearchResultSchema = z.object({
  requestId: z.number(),
  detailId: z.number(),
  requestName: z.string().nullable(),
  kind: z.string().nullable(),
  prefecture: z.string().nullable(),
  city: z.string().nullable(),
  location: z.string().nullable(),
  number: z.string().nullable(),
  buildingName: z.string().nullable(),
  unitName: z.string().nullable(),
  landCategory: z.string().nullable(),
  buildingTypes: z.array(z.string()),
  structureCategory: z.string().nullable(),
  landAreaM2: z.string().nullable(),
  landAreaSource: z.string().nullable(),
  floorAreaM2: z.string().nullable(),
  newConstructionDate: z.string().nullable(),
  isClosed: z.boolean(),
  pictureAcquiredAt: z.string().nullable(),
  currentOwners: z.object({ names: z.array(z.string()), count: z.number() }),
  ownerJudgementStatus: judgementSchema,
  ownerEmptyReason: emptyReasonSchema,
  ownerJudgementReason: judgementReasonSchema,
  activeSecurities: z.object({
    count: z.number(),
    top: z
      .object({
        rightKind: z.string().nullable(),
        holder: z.string().nullable(),
        currentAmountYen: z.number().nullable(),
      })
      .nullable(),
  }),
  securityJudgementStatus: judgementSchema,
  securityEmptyReason: emptyReasonSchema,
  securityJudgementReason: judgementReasonSchema,
  dispositionRestrictions: z.array(z.string()),
  matched: z.array(z.string()),
});
export type V2SearchResult = z.infer<typeof v2SearchResultSchema>;

export const v2SearchResponseSchema = z.object({
  results: z.array(v2SearchResultSchema),
  page: z.number(),
  pageSize: z.number(),
  hasNext: z.boolean(),
  total: z.object({ value: z.number(), isLowerBound: z.boolean() }),
});
export type V2SearchResponse = z.infer<typeof v2SearchResponseSchema>;

const words = z.array(z.string());

export const v2SearchChoicesSchema = z.object({
  landCategories: words,
  buildingTypes: words,
  structureCategories: z.array(
    z.object({ value: z.string(), label: z.string() })
  ),
  landAreaSources: words,
  newConstructionDateStatuses: words,
  siteRightKinds: words,
  ownerCautions: words,
  dispositionRestrictions: words,
  kokuPurposeRightKinds: words,
  kokuPurposeActions: words,
  kokuReasonWords: words,
  securityRightKinds: words,
  otsukuReasonWords: words,
  judgementStatuses: words,
});
export type V2SearchChoices = z.infer<typeof v2SearchChoicesSchema>;

/** Used until (or if never) the choices API answers; free-text vocabularies start empty. */
export const DEFAULT_V2_SEARCH_CHOICES: V2SearchChoices = {
  landCategories: [],
  buildingTypes: [],
  structureCategories: [
    { value: "木造", label: "木造" },
    { value: "軽量鉄骨造", label: "軽量鉄骨造" },
    { value: "鉄骨造", label: "鉄骨造" },
    { value: "RC", label: "鉄筋コンクリート造" },
    { value: "SRC", label: "鉄骨鉄筋コンクリート造" },
    { value: "ブロック造", label: "ブロック造" },
    { value: "その他", label: "その他" },
  ],
  landAreaSources: ["登記㎡", "旧単位換算", "複数成分", "不明"],
  newConstructionDateStatuses: ["日付あり", "年月日不詳", "記載なし・読めない"],
  siteRightKinds: ["所有権", "賃借権", "地上権"],
  ownerCautions: [],
  dispositionRestrictions: ["差押", "参加差押", "仮差押", "仮処分"],
  kokuPurposeRightKinds: [],
  kokuPurposeActions: [],
  kokuReasonWords: [],
  securityRightKinds: [],
  otsukuReasonWords: [],
  judgementStatuses: [...JUDGEMENT_STATUSES],
};

export const searchV2 = async (
  client: AxiosInstance,
  request: V2SearchRequest
): Promise<V2SearchResponse> =>
  v2SearchResponseSchema.parse(
    (await client.post<unknown>(V2_SEARCH_URL, request)).data
  );

export const fetchV2SearchChoices = async (
  client: AxiosInstance
): Promise<V2SearchChoices> =>
  v2SearchChoicesSchema.parse(
    (await client.get<unknown>(V2_SEARCH_CHOICES_URL)).data
  );
